package com.arena_web.ui.sections.eventdetail

import androidx.compose.runtime.*
import com.arena_web.core.viewmodel.PendingFile
import com.arena_web.core.viewmodel.UserParticipation
import com.arena_web.data.dto.EventParticipantDto
import com.arena_web.data.dto.MessageDto
import com.arena_web.ui.components.cards.ChatPlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.chat.EditingBar
import com.arena_web.ui.components.chat.FilePreviewBar
import com.arena_web.ui.components.chat.GroupPosition
import com.arena_web.ui.components.chat.MessageBubble
import com.arena_web.ui.components.chat.MessageInputBar
import com.arena_web.ui.components.chat.PinnedMessageBar
import com.arena_web.ui.components.chat.ReplyBar
import com.arena_web.ui.components.widgets.InlineSpinner
import com.arena_web.ui.components.widgets.LoadingSpinner
import kotlinx.browser.document
import org.w3c.dom.events.Event as DomEvent
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.icons.TintedGlyph
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.css.*

@Composable
fun EventChatPanel(
    messages: List<MessageDto>,
    messageText: String,
    isLoading: Boolean,
    onTextChanged: (String) -> Unit,
    onSend: () -> Unit,
    onTyping: () -> Unit,
    onLoadMore: () -> Unit,
    hasMore: Boolean,
    participants: List<EventParticipantDto> = emptyList(),
    sportName: String = "football",
    userParticipation: UserParticipation = UserParticipation.CONFIRMED,
    isPrivateEvent: Boolean = false,
    onViewProfile: (String) -> Unit = {},
    onJoinClick: () -> Unit = {},
    pinnedMessage: MessageDto? = null,
    replyingToMessage: MessageDto? = null,
    editingMessage: MessageDto? = null,
    pendingFile: PendingFile? = null,
    uploadingFile: Boolean = false,
    onReaction: ((String, String) -> Unit)? = null,
    onReply: ((MessageDto) -> Unit)? = null,
    onCancelReply: () -> Unit = {},
    onEdit: ((MessageDto) -> Unit)? = null,
    onCancelEdit: () -> Unit = {},
    onSubmitEdit: () -> Unit = {},
    onDelete: ((String) -> Unit)? = null,
    onPin: ((String) -> Unit)? = null,
    onAttachmentClick: (() -> Unit)? = null,
    onSendFile: () -> Unit = {},
    onCancelFile: () -> Unit = {},
    // Overrides the panel's own blue→dark gradient surface. The mobile chat
    // sheet passes the BottomSheet fill (#09142d) so the panel blends with the
    // global sheet background instead of showing its desktop-card gradient.
    backgroundOverride: String? = null,
    modifier: Modifier = Modifier,
) {
    val t = LocalAppStrings.current
    val currentUserId = remember { Storage.get(StorageKeys.USER_ID) }
    val participantMap = remember(participants) { participants.associateBy { it.userId } }
    val usePlayerCards = participants.isNotEmpty()

    // Figma node 7069:773468 — Main Block Container. Outer surface uses a soft
    // blue → near-black gradient, the inner Tracker section gets the rounded
    // top corners + 1px navy border (left/right/top).
    Column(
        modifier = modifier
            .fillMaxWidth()
            .sBorderRadius(16)
            .styleModifier {
                property(
                    "background",
                    backgroundOverride
                        ?: "linear-gradient(171.14deg, rgba(60, 121, 241, 0.135) 29.8%, rgba(2, 4, 11, 0.3) 84.74%)"
                )
                property("overflow", "hidden")
                property("display", "flex")
                property("flex-direction", "column")
                property("padding-bottom", s(12))
            }
    ) {
        // Header — title left, Live · N Players right (single row).
        // Padding 22 top, 16 sides. Pending-approval participants are
        // excluded from the count — only confirmed teammates show up.
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("flex-shrink", "0")
                    property("padding", "${s(22)} ${s(16)} 0")
                },
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(
                t.eventChatTitle,
                modifier = Modifier
                    .sFontSize(24)
                    .fontWeight(FontWeight.Medium)
                    .color(AppColors.Text.Secondary)
                    .styleModifier {
                        property("letter-spacing", s(0.24))
                        property("line-height", s(32))
                    }
            )
            ChatHeaderStatusRow(
                playersCount = participants.count { it.status.uppercase() == "CONFIRMED" }
            )
        }

        // Gated states: user hasn't joined / pending approval. Show the
        // locked hero (lock icon + title + subtitle + Join Match CTA) and
        // keep the input bar at the bottom in a visual-only "disabled"
        // state — per Figma 10051:166699 the bar is still visible.
        if (userParticipation != UserParticipation.CONFIRMED) {
            ChatGatedState(
                userParticipation = userParticipation,
                isPrivateEvent = isPrivateEvent,
                onJoinClick = onJoinClick,
                modifier = Modifier.fillMaxWidth().weight(1f),
            )
            MessageInputBar(
                messageText = "",
                onTextChanged = {},
                onSend = {},
                onTyping = {},
                disabled = true,
            )
            return@Column
        }

        // No-messages state — also a full-block centered layout so the icon
        // sits in the middle of the panel rather than at the scroll bottom.
        if (!isLoading && messages.isEmpty()) {
            ChatEmptyMessagesState(modifier = Modifier.fillMaxWidth().weight(1f))
            if (pendingFile != null) {
                FilePreviewBar(
                    file = pendingFile,
                    uploading = uploadingFile,
                    onSend = onSendFile,
                    onCancel = onCancelFile,
                )
            }
            if (replyingToMessage != null) {
                ReplyBar(message = replyingToMessage, onCancel = onCancelReply)
            }
            if (editingMessage != null) {
                EditingBar(message = editingMessage, onCancel = onCancelEdit)
            }
            MessageInputBar(
                messageText = messageText,
                onTextChanged = onTextChanged,
                onSend = if (editingMessage != null) onSubmitEdit else onSend,
                onTyping = onTyping,
                isEditing = editingMessage != null,
                onAttachmentClick = onAttachmentClick,
            )
            return@Column
        }

        // Messages area — gap 24 between message rows, padding sides 16 to
        // line up with the Figma header.
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f)
                .then(com.arena_web.ui.components.widgets.HiddenScrollbarStyle.toModifier())
                .styleModifier {
                    property("overflow-y", "auto")
                    property("display", "flex")
                    property("flex-direction", "column-reverse")
                    property("min-height", s(200))
                    property("padding-top", s(24))
                }
                .id("event-chat-messages-container")
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier { property("padding", "0 ${s(16)}") }
                    .sGap(24)
            ) {
                if (isLoading && messages.isNotEmpty()) {
                    Box(
                        modifier = Modifier.fillMaxWidth().sPadding(8, 0),
                        contentAlignment = Alignment.Center,
                    ) {
                        InlineSpinner(size = 20.px)
                    }
                }

                if (isLoading && messages.isEmpty()) {
                    Box(
                        modifier = Modifier.fillMaxWidth().sHeight(160),
                        contentAlignment = Alignment.Center
                    ) {
                        LoadingSpinner(size = 60.px)
                    }
                } else if (usePlayerCards) {
                    // Group consecutive messages by sender
                    val groups = mutableListOf<List<IndexedValue<MessageDto>>>()
                    var currentGroup = mutableListOf<IndexedValue<MessageDto>>()
                    messages.forEachIndexed { index, message ->
                        if (currentGroup.isEmpty() || currentGroup.last().value.senderId == message.senderId) {
                            currentGroup.add(IndexedValue(index, message))
                        } else {
                            groups.add(currentGroup)
                            currentGroup = mutableListOf(IndexedValue(index, message))
                        }
                    }
                    if (currentGroup.isNotEmpty()) groups.add(currentGroup)

                    groups.forEach { group ->
                        val firstMsg = group.first().value
                        val isOwn = firstMsg.senderId == currentUserId
                        val participant = participantMap[firstMsg.senderId]
                        val displayName = (participant?.fullName?.split(" ")?.lastOrNull()
                            ?: participant?.username ?: firstMsg.senderUsername ?: "").uppercase()

                        Div(attrs = {
                            style {
                                property("display", "flex")
                                property("flex-direction", "row")
                                property("justify-content", if (isOwn) "flex-end" else "flex-start")
                                property("align-items", "flex-end")
                                property("width", "100%")
                                property("gap", s(4))
                            }
                        }) {
                            if (!isOwn) {
                                ChatPlayerCard(
                                    playerName = displayName,
                                    sport = sportName,
                                    size = PlayerCardSize.XtraSmall,
                                    // Removed senders are gone from participantMap —
                                    // fall back to the card kit carried on the message.
                                    avatarUrl = participant?.cardAvatarUrl ?: firstMsg.senderCardAvatarUrl,
                                    cardAssetsBasePath = participant?.cardAssetsBasePath ?: firstMsg.senderCardAssetsBasePath,
                                    cardTextColor = participant?.cardTextColor ?: firstMsg.senderCardTextColor,
                                    modifier = Modifier.cursor(Cursor.Pointer).onClick { onViewProfile(firstMsg.senderId) },
                                )
                            }

                            Div(attrs = {
                                style {
                                    property("display", "flex")
                                    property("flex-direction", "column")
                                    property("gap", s(2))
                                    property("flex", "1")
                                    property("min-width", "0")
                                }
                            }) {
                                group.forEach { (index, message) ->
                                    val prevMessage = messages.getOrNull(index - 1)
                                    val nextMessage = messages.getOrNull(index + 1)
                                    val sameSenderAsPrev = prevMessage?.senderId == message.senderId
                                    val sameSenderAsNext = nextMessage?.senderId == message.senderId

                                    val groupPosition = when {
                                        !sameSenderAsPrev && !sameSenderAsNext -> GroupPosition.SINGLE
                                        !sameSenderAsPrev && sameSenderAsNext -> GroupPosition.FIRST
                                        sameSenderAsPrev && sameSenderAsNext -> GroupPosition.MIDDLE
                                        else -> GroupPosition.LAST
                                    }

                                    MessageBubble(
                                        message = message,
                                        isOwnMessage = isOwn,
                                        isGroupChat = true,
                                        showSenderInfo = false,
                                        groupPosition = groupPosition,
                                        currentUserId = currentUserId,
                                        usePlayerCard = true,
                                        onEdit = if (isOwn && onEdit != null) {{ onEdit(message) }} else null,
                                        onDelete = if (isOwn && onDelete != null) {{ onDelete(message.id) }} else null,
                                        onReaction = if (onReaction != null) {{ emoji -> onReaction(message.id, emoji) }} else null,
                                        onReply = if (onReply != null) {{ onReply(message) }} else null,
                                        onPin = if (onPin != null) {{ onPin(message.id) }} else null,
                                    )
                                }
                            }

                            if (isOwn) {
                                ChatPlayerCard(
                                    playerName = displayName,
                                    sport = sportName,
                                    size = PlayerCardSize.XtraSmall,
                                    avatarUrl = participant?.cardAvatarUrl ?: firstMsg.senderCardAvatarUrl,
                                    cardAssetsBasePath = participant?.cardAssetsBasePath ?: firstMsg.senderCardAssetsBasePath,
                                    cardTextColor = participant?.cardTextColor ?: firstMsg.senderCardTextColor,
                                    modifier = Modifier.cursor(Cursor.Pointer).onClick { onViewProfile(firstMsg.senderId) },
                                )
                            }
                        }
                    }
                } else {
                    // No player cards — render messages individually
                    messages.forEachIndexed { index, message ->
                        val isOwn = message.senderId == currentUserId
                        val prevMessage = messages.getOrNull(index - 1)
                        val nextMessage = messages.getOrNull(index + 1)
                        val sameSenderAsPrev = prevMessage?.senderId == message.senderId
                        val sameSenderAsNext = nextMessage?.senderId == message.senderId

                        val groupPosition = when {
                            !sameSenderAsPrev && !sameSenderAsNext -> GroupPosition.SINGLE
                            !sameSenderAsPrev && sameSenderAsNext -> GroupPosition.FIRST
                            sameSenderAsPrev && sameSenderAsNext -> GroupPosition.MIDDLE
                            else -> GroupPosition.LAST
                        }

                        MessageBubble(
                            message = message,
                            isOwnMessage = isOwn,
                            isGroupChat = true,
                            showSenderInfo = !sameSenderAsPrev,
                            groupPosition = groupPosition,
                            currentUserId = currentUserId,
                            onEdit = if (isOwn && onEdit != null) {{ onEdit(message) }} else null,
                            onDelete = if (isOwn && onDelete != null) {{ onDelete(message.id) }} else null,
                            onReaction = if (onReaction != null) {{ emoji -> onReaction(message.id, emoji) }} else null,
                            onReply = if (onReply != null) {{ onReply(message) }} else null,
                            onPin = if (onPin != null) {{ onPin(message.id) }} else null,
                        )
                    }
                }
            }
        }

        // Auto-scroll to newest. With flex-direction column-reverse, the bottom
        // of the chat is scrollTop = 0. We scroll when:
        //  - the new last message is mine (always)
        //  - the new last message is someone else's AND we were close to the
        //    bottom (< ~150px). Otherwise the user is browsing history, so we
        //    leave the viewport alone.
        // Tracks last message ID, not size, so loadMore (which prepends older
        // pages) doesn't trigger a scroll.
        val lastMessageId = messages.lastOrNull()?.id
        var previousLastId by remember { mutableStateOf<String?>(null) }
        LaunchedEffect(lastMessageId) {
            if (lastMessageId == null || lastMessageId == previousLastId) {
                previousLastId = lastMessageId
                return@LaunchedEffect
            }
            val isFirstLoad = previousLastId == null
            val container = document.getElementById("event-chat-messages-container")
            val newIsOwn = messages.lastOrNull()?.senderId == currentUserId
            // ~5 messages worth of slack: text bubbles run ~50px + 24px gap, so
            // five rows ≈ 370px. Bumped to 400 to also catch a row or two of
            // player-card avatars without dropping the auto-scroll.
            val nearBottom = container?.let { it.scrollTop > -400.0 } ?: true
            if (isFirstLoad || newIsOwn || nearBottom) {
                kotlinx.browser.window.setTimeout({
                    document.getElementById("event-chat-messages-container")?.scrollTop = 0.0
                }, 50)
            }
            previousLastId = lastMessageId
        }

        // Scroll near the top → load older page. Container uses flex-direction
        // column-reverse, so scrollTop is negative; the "top" is when scrollTop
        // approaches -(scrollHeight - clientHeight).
        LaunchedEffect(hasMore, isLoading) {
            val handler: (DomEvent) -> Unit = { _ ->
                val container = document.getElementById("event-chat-messages-container")
                if (container != null && hasMore && !isLoading) {
                    val scrollTop = container.scrollTop
                    val scrollHeight = container.scrollHeight.toDouble()
                    val clientHeight = container.clientHeight.toDouble()
                    if (scrollTop < -(scrollHeight - clientHeight - 100)) {
                        onLoadMore()
                    }
                }
            }
            document.getElementById("event-chat-messages-container")
                ?.addEventListener("scroll", handler)
        }

        // Pinned message bar
        if (pinnedMessage != null) {
            PinnedMessageBar(message = pinnedMessage)
        }

        // File preview bar
        if (pendingFile != null) {
            FilePreviewBar(
                file = pendingFile,
                uploading = uploadingFile,
                onSend = onSendFile,
                onCancel = onCancelFile,
            )
        }

        // Reply bar
        if (replyingToMessage != null) {
            ReplyBar(message = replyingToMessage, onCancel = onCancelReply)
        }

        // Edit indicator bar
        if (editingMessage != null) {
            EditingBar(message = editingMessage, onCancel = onCancelEdit)
        }

        // Input bar
        MessageInputBar(
            messageText = messageText,
            onTextChanged = onTextChanged,
            onSend = if (editingMessage != null) onSubmitEdit else onSend,
            onTyping = onTyping,
            isEditing = editingMessage != null,
            onAttachmentClick = onAttachmentClick,
        )
    }
}

@Composable
private fun ChatHeaderStatusRow(playersCount: Int) {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier.sGap(12),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Row(modifier = Modifier.sGap(8), verticalAlignment = Alignment.CenterVertically) {
            Box(
                modifier = Modifier
                    .sSize(6)
                    .sBorderRadius(100)
                    .styleModifier { property("background", "#00E5A4") }
            )
            SpanText(
                t.eventChatLive,
                modifier = Modifier
                    .sFontSize(18)
                    .fontWeight(FontWeight.Medium)
                    .color(AppColors.Text.Secondary)
                    .styleModifier {
                        property("letter-spacing", s(0.09))
                        property("line-height", s(22))
                    }
            )
        }
        Box(
            modifier = Modifier
                .sSize(2)
                .sBorderRadius(100)
                .backgroundColor(AppColors.Text.Secondary)
        )
        Row(modifier = Modifier.sGap(8), verticalAlignment = Alignment.CenterVertically) {
            SpanText(
                playersCount.toString(),
                modifier = Modifier
                    .sFontSize(18)
                    .fontWeight(FontWeight.Medium)
                    .color(AppColors.Text.Secondary)
                    .styleModifier {
                        property("letter-spacing", s(0.09))
                        property("line-height", s(22))
                    }
            )
            SpanText(
                t.eventChatPlayersLabel,
                modifier = Modifier
                    .sFontSize(18)
                    .fontWeight(FontWeight.Medium)
                    .color(AppColors.Text.Secondary)
                    .styleModifier {
                        property("letter-spacing", s(0.09))
                        property("line-height", s(22))
                    }
            )
        }
    }
}

@Composable
private fun ChatGatedState(
    userParticipation: UserParticipation,
    isPrivateEvent: Boolean,
    onJoinClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    // Wording depends on whether the user is still waiting for admin approval
    // (PENDING on a private event) or hasn't joined at all (NONE / REJECTED).
    // The PENDING flow hides the Join CTA because the request is already in
    // flight; NONE / REJECTED keeps the Figma button.
    val t = LocalAppStrings.current
    val showJoinButton = userParticipation != UserParticipation.PENDING
    val title = when {
        userParticipation == UserParticipation.PENDING && isPrivateEvent -> t.eventChatWaitingApproval
        userParticipation == UserParticipation.PENDING -> t.eventChatConfirmingSpot
        else -> t.eventChatLocked
    }
    val subtitle = when {
        userParticipation == UserParticipation.PENDING && isPrivateEvent ->
            t.eventChatWaitingApprovalSubtitle
        userParticipation == UserParticipation.PENDING ->
            t.eventChatConfirmingSpotSubtitle
        isPrivateEvent ->
            t.eventChatPrivateSubtitle
        else ->
            t.eventChatLockedSubtitle
    }

    Box(
        modifier = modifier.styleModifier {
            property("display", "flex")
            property("justify-content", "center")
            property("align-items", "flex-start")
            property("padding-top", s(80))
        },
    ) {
        Column(
            modifier = Modifier.sGap(18).sPadding(0, 16),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            // Big lock icon — Figma 10053:168449. Trimmed slightly under
            // the natural 67.5×90 to read a touch more compact.
            Icon(
                src = AppIcons.LockChat,
                modifier = Modifier
                    .sWidth(57).sHeight(76)
                    .styleModifier { property("flex-shrink", "0") },
            )

            Column(
                modifier = Modifier.sGap(40),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Column(
                    modifier = Modifier.sGap(18),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    SpanText(
                        title,
                        modifier = Modifier
                            .sFontSize(26)
                            .fontWeight(FontWeight.SemiBold)
                            .color(Color("#BED6F5"))
                            .styleModifier {
                                property("font-family", "Urbanist, sans-serif")
                                property("line-height", s(32))
                                property("letter-spacing", s(0.52))
                                property("text-align", "center")
                                property("white-space", "nowrap")
                            },
                    )
                    SpanText(
                        subtitle,
                        modifier = Modifier
                            .sFontSize(18)
                            .fontWeight(FontWeight.Medium)
                            .color(Color("#99B1BF"))
                            .styleModifier {
                                property("font-family", "Urbanist, sans-serif")
                                property("line-height", s(26))
                                property("letter-spacing", s(0.18))
                                property("text-align", "center")
                                property("max-width", s(400))
                            },
                    )
                }

                if (showJoinButton) {
                    JoinMatchButton(onClick = onJoinClick)
                }
            }
        }
    }
}

/**
 * Pill button used inside the Chat-locked hero — Figma 10053:168463.
 * Filled `#0061FF` with a darker `#004DCD` circle on the right holding a
 * 45°-rotated arrow-up glyph (reads as "open / go to match").
 */
@Composable
private fun JoinMatchButton(onClick: () -> Unit) {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier
            .sHeight(46)
            .sBorderRadius(100)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("background-color", "#0061FF")
                property("display", "flex")
                property("align-items", "center")
                property("gap", s(10))
                property("padding", "${s(8)} ${s(4)} ${s(8)} ${s(22)}")
                property("box-sizing", "border-box")
                property("white-space", "nowrap")
            },
    ) {
        SpanText(
            t.eventJoinEvent,
            modifier = Modifier
                .sFontSize(16)
                .fontWeight(FontWeight.Medium)
                .color(Color("#FFFFFF"))
                .styleModifier {
                    property("font-family", "Urbanist, sans-serif")
                    property("line-height", s(18))
                    property("letter-spacing", s(0.032))
                },
        )
        Box(
            modifier = Modifier
                .sSize(38)
                .sBorderRadius(100)
                .styleModifier {
                    property("background-color", "#004DCD")
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                    property("flex-shrink", "0")
                },
        ) {
            Icon(
                src = AppIcons.ArrowUp,
                modifier = Modifier
                    .sWidth(17).sHeight(17)
                    .styleModifier {
                        property("transform", "rotate(45deg)")
                        property("filter", "brightness(0) invert(1)")
                    },
            )
        }
    }
}

@Composable
private fun ChatEmptyMessagesState(modifier: Modifier = Modifier) {
    val t = LocalAppStrings.current
    Box(modifier = modifier, contentAlignment = Alignment.Center) {
        Column(
            modifier = Modifier.sGap(12).sPadding(24),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            TintedGlyph(src = AppIcons.Chat, sizePx = 40)
            SpanText(
                t.eventChatNoMessages,
                modifier = Modifier
                    .sFontSize(15)
                    .fontWeight(FontWeight.Medium)
                    .color(AppColors.Text.Primary),
            )
            SpanText(
                t.eventChatNoMessagesSubtitle,
                modifier = Modifier
                    .sFontSize(12)
                    .color(AppColors.Text.Secondary)
                    .styleModifier { property("text-align", "center") },
            )
        }
    }
}
